import React, { useEffect, useState } from 'react';
// @ts-ignore
import styles from './HealthData.module.scss';
import useHealthData, {
	IHealthData,
} from '../../../Modules/HealthData/Hooks/useHealthData';
import { useEffectAsync } from '../../../helpers';
import HealthDataBloodGlucoseTable from '../../../Modules/HealthData/Components/Tables/HealthDataBloodGlucoseTable';
import { HealthDataFilterFormValues } from './Filter/HealthDataFilterForm';
import SideFilter from '../../../Components/Filter/SideFilter';
import { CHARTS_DATASET } from '../Utils/chartsDataset';
import { Chart } from '../Enums/Chart.enum';
import HealthDataChart from './Charts/HealthDataChart';
import HealthDataTable from './Tables/HealthDataTable';
import { Table } from '../Enums/Table.enum';
import { TABLE_DATASET } from '../Utils/tableDataset';
import HealthDataVerticalTable from './Tables/HealthDataVerticalTable';
import { getMomentDate } from '../../../Utils/dates.util';
import QuestionnairesResults from '../../Questionnaire/Components/QuestionnaireResults/QuestionnairesResults';
import { QuestionnairesResultsReStructureScheme } from '../../Questionnaire/interfaces';
import { sortDataForGlucoseChart } from '../Utils/chartSorting';

interface HealthDataFilter {
	onSubmit: (values: HealthDataFilterFormValues) => void;
}

interface HealthDataProps {
	patientId: string;
	FilterComponent: React.FC<HealthDataFilter>;
	questionnairesResultsStructure?: QuestionnairesResultsReStructureScheme[];
}

interface HealthDataKeys {
	bloodGlucose: string[];
	bloodPressure: string[];
	weight: string[];
	height: string[];
	waistline: string[];
	steps: string[];
	breathingRate: string[];
	oxygenSaturation: string[];
	sleep: string[];
	hemoglobin: string[];
	hemoglobinA1C: string[];
	stressLevel: string[];
	activeEnergy: string[];
	distanceWalkingRunning: string[];
	workout: string[];
	multiple: string[];
	questionnairesCodes: string[];
}

interface HealthDataDataset {
	bloodGlucose: IHealthData[];
	bloodPressure: IHealthData[];
	weight: IHealthData[];
	height: IHealthData[];
	waistline: IHealthData[];
	steps: IHealthData[];
	breathingRate: IHealthData[];
	oxygenSaturation: IHealthData[];
	sleep: IHealthData[];
	hemoglobin: IHealthData[];
	hemoglobinA1C: IHealthData[];
	stressLevel: IHealthData[];
	activeEnergy: IHealthData[];
	distanceWalkingRunning: IHealthData[];
	workout: IHealthData[];
	multiple: IHealthData[];
}

const HealthData: React.FC<HealthDataProps> = props => {
	const { patientId, FilterComponent, questionnairesResultsStructure } = props;
	const { getHealthData, filterHealthDataByKeys, filterKeys } = useHealthData();
	const [isLoading, setIsLoading] = useState<boolean>(true);
	const [filter, setFilter] = useState<HealthDataFilterFormValues>();

	const datasetDefaults = {
		bloodGlucose: [],
		bloodPressure: [],
		weight: [],
		height: [],
		waistline: [],
		steps: [],
		breathingRate: [],
		oxygenSaturation: [],
		sleep: [],
		hemoglobin: [],
		hemoglobinA1C: [],
		stressLevel: [],
		activeEnergy: [],
		distanceWalkingRunning: [],
		workout: [],
		multiple: [],
		questionnairesCodes: [],
	};

	const [showDataset, setShowDataset] =
		useState<HealthDataKeys>(datasetDefaults);
	const [dataset, setDataset] = useState<HealthDataDataset>(datasetDefaults);

	useEffect(() => {
		if (filter) {
			setShowDataset({
				bloodGlucose: filterKeys(
					filter.keys,
					CHARTS_DATASET[Chart.BLOOD_GLUCOSE].keys,
				),
				bloodPressure: filterKeys(
					filter.keys,
					CHARTS_DATASET[Chart.BLOOD_PRESSURE].keys,
				),
				weight: filterKeys(filter.keys, CHARTS_DATASET[Chart.WEIGHT].keys),
				waistline: filterKeys(
					filter.keys,
					CHARTS_DATASET[Chart.WAISTLINE].keys,
				),
				steps: filterKeys(filter.keys, CHARTS_DATASET[Chart.STEPS].keys),
				breathingRate: filterKeys(
					filter.keys,
					CHARTS_DATASET[Chart.BREATHING_RATE].keys,
				),
				oxygenSaturation: filterKeys(
					filter.keys,
					CHARTS_DATASET[Chart.OXYGEN_SATURATION].keys,
				),
				sleep: filterKeys(filter.keys, CHARTS_DATASET[Chart.SLEEP].keys),
				hemoglobin: filterKeys(
					filter.keys,
					CHARTS_DATASET[Chart.HEMOGLOBIN].keys,
				),
				hemoglobinA1C: filterKeys(
					filter.keys,
					CHARTS_DATASET[Chart.HEMOGLOBIN_A1C].keys,
				),
				stressLevel: filterKeys(
					filter.keys,
					CHARTS_DATASET[Chart.STRESS_LEVEL].keys,
				),
				activeEnergy: filterKeys(
					filter.keys,
					CHARTS_DATASET[Chart.ACTIVE_ENERGY].keys,
				),
				distanceWalkingRunning: filterKeys(
					filter.keys,
					CHARTS_DATASET[Chart.DISTANCE_WALKING_RUNNING].keys,
				),
				workout: filterKeys(filter.keys, CHARTS_DATASET[Chart.WORKOUT].keys),
				height: filterKeys(filter.keys, CHARTS_DATASET[Chart.HEIGHT].keys),
				multiple: filterKeys(filter.keys, TABLE_DATASET[Table.MULTIPLE].keys),
				questionnairesCodes: filterKeys(
					filter.keys,
					TABLE_DATASET[Table.QUESTIONNAIRE_RESULTS_LATAM_BABY].keys,
				),
			});
		}
	}, [filter]);

	// @ts-ignore
	useEffectAsync(async () => {
		if (filter) {
			setIsLoading(true);
			const data: IHealthData[] = await getHealthData(
				patientId,
				filter.view,
				{
					from: getMomentDate(filter.from),
					to: getMomentDate(filter.to),
				},
				filter.source,
				filter.keys,
				filter.type,
			);
			if (data) {
				const filterData = {
					bloodGlucose: sortDataForGlucoseChart(
						filterHealthDataByKeys(
							data,
							CHARTS_DATASET[Chart.BLOOD_GLUCOSE].keys,
						),
					),
					bloodPressure: filterHealthDataByKeys(
						data,
						CHARTS_DATASET[Chart.BLOOD_PRESSURE].keys,
					),
					weight: filterHealthDataByKeys(
						data,
						CHARTS_DATASET[Chart.WEIGHT].keys,
					),
					waistline: filterHealthDataByKeys(
						data,
						CHARTS_DATASET[Chart.WAISTLINE].keys,
					),
					steps: filterHealthDataByKeys(data, CHARTS_DATASET[Chart.STEPS].keys),
					breathingRate: filterHealthDataByKeys(
						data,
						CHARTS_DATASET[Chart.BREATHING_RATE].keys,
					),
					oxygenSaturation: filterHealthDataByKeys(
						data,
						CHARTS_DATASET[Chart.OXYGEN_SATURATION].keys,
					),
					sleep: filterHealthDataByKeys(data, CHARTS_DATASET[Chart.SLEEP].keys),
					hemoglobin: filterHealthDataByKeys(
						data,
						CHARTS_DATASET[Chart.HEMOGLOBIN].keys,
					),
					hemoglobinA1C: filterHealthDataByKeys(
						data,
						CHARTS_DATASET[Chart.HEMOGLOBIN_A1C].keys,
					),
					stressLevel: filterHealthDataByKeys(
						data,
						CHARTS_DATASET[Chart.STRESS_LEVEL].keys,
					),
					activeEnergy: filterHealthDataByKeys(
						data,
						CHARTS_DATASET[Chart.ACTIVE_ENERGY].keys,
					),
					distanceWalkingRunning: filterHealthDataByKeys(
						data,
						CHARTS_DATASET[Chart.DISTANCE_WALKING_RUNNING].keys,
					),
					workout: filterHealthDataByKeys(
						data,
						CHARTS_DATASET[Chart.WORKOUT].keys,
					),
					height: filterHealthDataByKeys(
						data,
						CHARTS_DATASET[Chart.HEIGHT].keys,
					),
					multiple: filterHealthDataByKeys(
						data,
						TABLE_DATASET[Table.MULTIPLE].keys,
					),
				};
				setDataset(filterData);
			}
			setIsLoading(false);
		}
	}, [filter]);

	return (
		<>
			<div className={styles.wrapper}>
				<div className={styles.filterWrapper}>
					<SideFilter className={styles.filter}>
						<FilterComponent
							onSubmit={(values: HealthDataFilterFormValues) =>
								setFilter(values)
							}
						/>
					</SideFilter>
				</div>
				<div className={'flex-grow-1 position-relative'}>
					<div className={styles.chartsWrapper}>
						{/* TABULKY */}
						{showDataset.questionnairesCodes.length > 0 && filter && (
							<QuestionnairesResults
								questionnairesCodes={showDataset.questionnairesCodes}
								patientId={patientId}
								structure={questionnairesResultsStructure}
								from={getMomentDate(filter.from)}
								to={getMomentDate(filter.to)}
								filterKeys={filter.keys}
							/>
						)}
						{showDataset.multiple.length > 0 && filter && (
							<HealthDataVerticalTable
								tableName={Table.MULTIPLE}
								healthData={dataset.multiple}
								isLoading={isLoading}
								filter={filter}
							/>
						)}
						{showDataset.bloodGlucose.length > 0 &&
							filter &&
							!filter.keys.includes('meddiBabyLatamGlycemia') && (
								<HealthDataBloodGlucoseTable
									healthData={dataset.bloodGlucose}
									isLoading={isLoading}
									filter={filter}
								/>
							)}
						{showDataset.bloodPressure.length > 0 && filter && (
							<HealthDataTable
								tableName={Table.BLOOD_PRESSURE}
								healthData={dataset.bloodPressure}
								isLoading={isLoading}
								filter={filter}
							/>
						)}
						{/* GRAFY */}
						{showDataset.steps.length > 0 && filter && (
							<HealthDataChart
								patientId={patientId}
								filter={filter}
								chartType={Chart.STEPS}
								healthData={dataset.steps}
								isLoading={isLoading}
							/>
						)}
						{showDataset.distanceWalkingRunning.length > 0 && filter && (
							<HealthDataChart
								patientId={patientId}
								filter={filter}
								chartType={Chart.DISTANCE_WALKING_RUNNING}
								healthData={dataset.distanceWalkingRunning}
								isLoading={isLoading}
							/>
						)}
						{showDataset.sleep.length > 0 && filter && (
							<HealthDataChart
								patientId={patientId}
								filter={filter}
								chartType={Chart.SLEEP}
								healthData={dataset.sleep}
								isLoading={isLoading}
							/>
						)}
						{showDataset.activeEnergy.length > 0 && filter && (
							<HealthDataChart
								patientId={patientId}
								filter={filter}
								chartType={Chart.ACTIVE_ENERGY}
								healthData={dataset.activeEnergy}
								isLoading={isLoading}
							/>
						)}
						{showDataset.workout.length > 0 && filter && (
							<HealthDataChart
								patientId={patientId}
								filter={filter}
								chartType={Chart.WORKOUT}
								healthData={dataset.workout}
								isLoading={isLoading}
							/>
						)}
						{showDataset.oxygenSaturation.length > 0 && filter && (
							<HealthDataChart
								patientId={patientId}
								filter={filter}
								chartType={Chart.OXYGEN_SATURATION}
								healthData={dataset.oxygenSaturation}
								isLoading={isLoading}
							/>
						)}
						{showDataset.weight.length > 0 && filter && (
							<HealthDataChart
								patientId={patientId}
								filter={filter}
								chartType={Chart.WEIGHT}
								healthData={dataset.weight}
								isLoading={isLoading}
							/>
						)}
						{showDataset.waistline.length > 0 && filter && (
							<HealthDataChart
								patientId={patientId}
								filter={filter}
								chartType={Chart.WAISTLINE}
								healthData={dataset.waistline}
								isLoading={isLoading}
							/>
						)}
						{showDataset.height.length > 0 && filter && (
							<HealthDataChart
								patientId={patientId}
								filter={filter}
								chartType={Chart.HEIGHT}
								healthData={dataset.height}
								isLoading={isLoading}
							/>
						)}
						{showDataset.stressLevel.length > 0 && filter && (
							<HealthDataChart
								patientId={patientId}
								filter={filter}
								chartType={Chart.STRESS_LEVEL}
								healthData={dataset.stressLevel}
								isLoading={isLoading}
							/>
						)}
						{showDataset.breathingRate.length > 0 && filter && (
							<HealthDataChart
								patientId={patientId}
								filter={filter}
								chartType={Chart.BREATHING_RATE}
								healthData={dataset.breathingRate}
								isLoading={isLoading}
							/>
						)}
						{showDataset.bloodPressure.length > 0 && filter && (
							<HealthDataChart
								patientId={patientId}
								filter={filter}
								chartType={Chart.BLOOD_PRESSURE}
								healthData={dataset.bloodPressure}
								isLoading={isLoading}
							/>
						)}
						{showDataset.hemoglobin.length > 0 && filter && (
							<HealthDataChart
								patientId={patientId}
								filter={filter}
								chartType={Chart.HEMOGLOBIN}
								healthData={dataset.hemoglobin}
								isLoading={isLoading}
							/>
						)}
						{showDataset.hemoglobinA1C.length > 0 && filter && (
							<HealthDataChart
								patientId={patientId}
								filter={filter}
								chartType={Chart.HEMOGLOBIN_A1C}
								healthData={dataset.hemoglobinA1C}
								isLoading={isLoading}
							/>
						)}
						{showDataset.bloodGlucose.length > 0 && filter && (
							<HealthDataChart
								patientId={patientId}
								filter={filter}
								chartType={Chart.BLOOD_GLUCOSE}
								healthData={dataset.bloodGlucose}
								isLoading={isLoading}
							/>
						)}
						{/*
                            <QuestionnaireResultTable patientId={patientId} from={showDataFrom} to={showDataTo} interval={viewInterval}/>
                        */}
					</div>
				</div>
			</div>
		</>
	);
};

export default HealthData;
